import { useRef, useState } from 'react';
import { normalizeOption } from '../utils/popupUtils';

const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_MIN_LENGTH = 3;

/**
 * Provides debounced search handlers for search-select and mention-textarea fields.
 *
 * Each handler uses AbortController (latest-request-wins): when a new search fires
 * before the previous one resolves, the stale response is discarded.
 *
 * @param {Object} params
 * @param {Object} params.api - { searchOptions?, getMentionOptions? }
 * @param {Object} params.popupContext
 * @param {Function} params.setDynamicOptions - Setter from usePopupOptions
 * @returns {{ mentionOptions: Object, getSearchHandler: Function, getMentionSearchHandler: Function }}
 */
export function usePopupSearch({ api, popupContext, setDynamicOptions }) {
  const [mentionOptions, setMentionOptions] = useState({});
  const searchTimerRef = useRef({});
  const searchAbortRef = useRef({});
  const mentionAbortRef = useRef({});

  /**
   * Returns a debounced `onSearch` handler for a search-select field,
   * or `undefined` if the field has no search fetch key.
   * @param {Object} field
   * @returns {Function|undefined}
   */
  function getSearchHandler(field) {
    if (!field.searchFetchKey && !field.searchFetcher) return undefined;

    const searchFn = api.searchOptions ?? api.searchFetcher;
    if (typeof searchFn !== 'function') return undefined;

    return (search) => {
      clearTimeout(searchTimerRef.current[field.name]);

      if (search.length < SEARCH_MIN_LENGTH) {
        setDynamicOptions((prev) => ({ ...prev, [field.name]: [] }));
        return;
      }

      searchTimerRef.current[field.name] = setTimeout(() => {
        // Cancel the previous in-flight request for this field
        searchAbortRef.current[field.name]?.abort();
        const controller = new AbortController();
        searchAbortRef.current[field.name] = controller;

        Promise.resolve(searchFn(field, search, popupContext))
          .then((data) => {
            if (controller.signal.aborted) return;
            const raw = Array.isArray(data) ? data : (data?.items ?? data?.list ?? []);
            setDynamicOptions((prev) => ({
              ...prev,
              [field.name]: raw.map((item) => normalizeOption(item, field)),
            }));
          })
          .catch((err) => {
            if (controller.signal.aborted) return;
            console.error(`[DynamicPopup] Search failed for field "${field.name}":`, err);
          });
      }, SEARCH_DEBOUNCE_MS);
    };
  }

  /**
   * Returns an `onSearch` handler for a mention-textarea field,
   * or `undefined` if no mention fetcher is configured.
   * @param {Object} field
   * @returns {Function|undefined}
   */
  function getMentionSearchHandler(field) {
    const mentionFn = api.getMentionOptions ?? api.mentionFetcher;
    if (typeof mentionFn !== 'function') return undefined;

    return (search) => {
      // Cancel the previous in-flight mention request for this field
      mentionAbortRef.current[field.name]?.abort();
      const controller = new AbortController();
      mentionAbortRef.current[field.name] = controller;

      Promise.resolve(mentionFn(field, search, popupContext))
        .then((data) => {
          if (controller.signal.aborted) return;
          const raw = Array.isArray(data) ? data : (data?.items ?? data?.list ?? []);
          setMentionOptions((prev) => ({
            ...prev,
            [field.name]: raw.map((item) => ({
              value: item.user_name ?? item.userName ?? item.username ?? String(item),
              label: item.user_name ?? item.userName ?? item.username ?? String(item),
            })),
          }));
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.error(`[DynamicPopup] Mention search failed for field "${field.name}":`, err);
        });
    };
  }

  return { mentionOptions, getSearchHandler, getMentionSearchHandler };
}
